/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   3/4/22
 */

import React from "react";
import {Autocomplete, CircularProgress, createFilterOptions, TextField} from "@mui/material";
import {RequestPolicy, useQuery} from "urql";

export interface TypeaheadControlProps {
	label?: string;
	values: string[],
	query: any
	free?: boolean
	onChange: (values: string[]) => void
	requestPolicy?: RequestPolicy
	disabled?: boolean
}

const filter = createFilterOptions<string>();

export default function TypeaheadControl({ query, label, values, onChange, free, requestPolicy, disabled }: TypeaheadControlProps) {

	const [search, setSearch] = React.useState("");

	const [autoCompleteResult] = useQuery<Record<string, string[]>, { search: string }>({
		query: query,
		variables: { search },
		requestPolicy: requestPolicy ?? "cache-first",
		pause: search.length < 3,
	});

	const { data, fetching } = autoCompleteResult;

	return (
		<Autocomplete
			multiple
			options={[
				...values,
				...(data?.[Object.keys(data).filter(i => i !== "errors")?.[0]] || [])
			]}
			disabled={disabled}
			loading={fetching}
			value={values}
			onChange={(_, v: string[]) => {
				if (v) {
					setSearch("");
					onChange(v);
				}
			}}
			onInputChange={(_, value) => {
				setSearch(value);
			}}
			ChipProps={{
				size: "small"
			}}
			clearOnEscape
			filterSelectedOptions
			fullWidth
			freeSolo={free}
			filterOptions={!free ? x => x : (options, params) => {
				const filtered = filter(options, params);

				const { inputValue } = params;
				// Suggest the creation of a new value
				const isExisting = options.some((option) => inputValue === option);
				if (inputValue !== '' && !isExisting) {
					filtered.push(inputValue);
				}

				return filtered;
			}}
			selectOnFocus={free}
			clearOnBlur={free}
			handleHomeEndKeys={free}
			isOptionEqualToValue={(option, value) => option === value}
			noOptionsText={search.length === 0 ? "Start typing to see options..." : undefined}
			renderInput={(params) => (
				<TextField
					{...params}
					variant="standard"
					label={label}
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<React.Fragment>
								{fetching ? <CircularProgress color="inherit" size={20}/> : null}
								{params.InputProps.endAdornment}
							</React.Fragment>
						),
					}}
				/>
			)}
		/>
	);

}